import React, { useMemo } from 'react'
import { useQuery } from 'react-query'

import { isArray } from 'lodash'

import { Tab, Tabs } from '@mui/material'
import { TabContext, TabPanel } from '@mui/lab'
import { Table } from '@shared/components/table/table.component'
import { usePersistedTabChange } from '@shared/hooks/use-persisted-tab-change'
import { useRequirementsData } from '@shared/hooks/use-requirements-data.hook'
import { apiJSON } from '@shared/utils/api/api.util'
import { originalIcons } from '@shared/components/icons/icons'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useRouter } from '@shared/hooks/use-router'
import { BaseRequirement, RequirementType } from '@shared/types'
import { useResourceRemove } from '@shared/hooks/use-resource-remove.hook'

import { RequirementsTable } from '@modules/requirement/requirement-table/requirements-table.component'
import { MaterialTableProps } from '@material-table/core'
import { useStatusesQuery } from '@shared/hooks/use-statuses-query.hook'

export function AdminRequirementsCrudPage() {
  const { navigate, location } = useRouter()
  const { statuses } = useStatusesQuery()

  const {
    isRemovalDialogOpened,
    isResourceRemoving,
    setRemovalDialogOpened,
    setRemovedResourceID,
    onResourceRemove,
    /**
     * @todo retrieve from config
     */
  } = useResourceRemove('requirementbase/crud')

  /**
   * @todo retrieve from admin-generic.consts
   */
  const { data: baseRequirements, isLoading: areRequirementsFetching } =
    useQuery<BaseRequirement[]>('requirementbase/crud', () =>
      apiJSON('requirementbase/crud')
    )

  const requirements = useMemo(
    () =>
      (baseRequirements ?? []).map((requirement) => ({
        ...requirement,
        groupID: requirement.group.groupID,
      })),
    [baseRequirements]
  )

  const { isRequirementsDataFetching, groupsPerScopeEntries, scopesList } =
    useRequirementsData<BaseRequirement>(requirements, true)

  const { currentTab, onTabChange } = usePersistedTabChange(
    'admin.requirements.currentTab'
  )
  const isDataLoading = isRequirementsDataFetching || areRequirementsFetching

  if (isDataLoading) {
    return (
      <Table
        title="Wymagania"
        columns={[]}
        data={[]}
        isLoading={isDataLoading}
      />
    )
  }

  return (
    <>
      <TabContext value={currentTab}>
        <Tabs
          value={currentTab}
          onChange={onTabChange}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
        >
          {scopesList.map((scopeName, index) => (
            <Tab
              key={scopeName}
              label={scopeName}
              value={index.toString()}
              wrapped
              style={{ paddingLeft: 14, paddingRight: 14 }}
            />
          ))}
        </Tabs>
        {groupsPerScopeEntries.map(
          ([scopeName, scopeRequirementsGroups], index) => (
            <TabPanel
              key={scopeName}
              value={index.toString()}
              style={{ padding: 0, paddingTop: 24 }}
            >
              <RequirementsTable
                enableGroupRowOrder
                enableColumnOrder
                enableColumnOffset
                enableShowNotActive
                isLoading={isDataLoading}
                requirementsGroups={scopeRequirementsGroups}
                initialScrollPosition="top"
                initiallyCollapsedWithButtons={false}
                statuses={statuses}
                hideAdditionalComponent
                actions={
                  [
                    {
                      icon: originalIcons.Add,
                      isFreeAction: true,
                      onClick: (event) => {
                        navigate('/admin/requirements/add', {
                          state: {
                            backgroundLocation: location,
                          },
                        })
                      },
                    },
                    {
                      icon: originalIcons.Palette,
                      tooltip: 'Szablony',
                      onClick: (
                        event: React.MouseEvent<HTMLElement>,
                        resourceEntity
                      ) => {
                        if (!isArray(resourceEntity)) {
                          navigate(
                            `/admin/templates?filterBy=requirementBase--requirementBaseID&filterValue=${resourceEntity.requirementBaseID}`
                          )
                        }
                      },
                    },
                    {
                      icon: originalIcons.Create,
                      tooltip: 'Edytuj',
                      onClick: (
                        event: React.MouseEvent<HTMLElement>,
                        resourceEntity
                      ) => {
                        if (!isArray(resourceEntity)) {
                          navigate(
                            `/admin/requirements/edit/${resourceEntity.requirementBaseID}`,
                            {
                              state: {
                                backgroundLocation: location,
                              },
                            }
                          )
                        }
                      },
                    },
                    {
                      icon: originalIcons.Delete,
                      tooltip: 'Usuń',
                      onClick: (
                        event: React.MouseEvent<HTMLElement>,
                        resourceEntity
                      ) => {
                        if (!isArray(resourceEntity)) {
                          setRemovedResourceID(
                            resourceEntity.requirementBaseID.toString()
                          )
                          setRemovalDialogOpened(true)
                        }
                      },
                    },
                  ] as MaterialTableProps<RequirementType>['actions']
                }
              />
            </TabPanel>
          )
        )}
      </TabContext>
      <ConfirmationDialog
        submitButtonProps={{
          children: 'Usuń',
          isLoading: isResourceRemoving,
        }}
        title="Potwierdzenie usunięcia"
        onClose={onResourceRemove}
        open={isRemovalDialogOpened}
      >
        Czy napewno chcesz usunąć dany wiersz?
      </ConfirmationDialog>
    </>
  )
}
